import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

export default class BaseRoom {
  components = {};
  
  // Texture cache to prevent reloading same textures when switching materials
  static textureCache = new Map();

  constructor() {
    this.textureLoader = new THREE.TextureLoader();

    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath("draco/");

    this.gltfLoader = new GLTFLoader();
    this.gltfLoader.setDRACOLoader(this.dracoLoader);

    this.slots = Object(this.slots_meshes || {}).keys;
  }

  /**
   * Load a texture with caching support.
   * Returns cached texture if already loaded, otherwise loads and caches it.
   * @param {string} path - Path to the texture file
   * @param {Object} options - Texture options (flipY, colorSpace, repeat, wrapS, wrapT, etc.)
   * @returns {THREE.Texture}
   */
  loadTexture(path, options = {}) {
    // Create cache key from path and relevant options
    const cacheKey = path;
    
    if (BaseRoom.textureCache.has(cacheKey)) {
      const cachedTexture = BaseRoom.textureCache.get(cacheKey);
      // Clone the texture to allow different repeat/offset settings per use
      const clonedTexture = cachedTexture.clone();
      clonedTexture.needsUpdate = true;
      
      // Apply instance-specific options
      this.applyTextureOptions(clonedTexture, options);
      return clonedTexture;
    }
    
    // Load new texture
    const texture = this.textureLoader.load(path);
    
    // Apply base options
    this.applyTextureOptions(texture, options);
    
    // Cache the base texture
    BaseRoom.textureCache.set(cacheKey, texture);
    
    return texture;
  }

  /**
   * Apply options to a texture
   * @param {THREE.Texture} texture
   * @param {Object} options
   */
  applyTextureOptions(texture, options) {
    if (options.flipY !== undefined) texture.flipY = options.flipY;
    if (options.colorSpace) texture.colorSpace = options.colorSpace;
    if (options.wrapS) texture.wrapS = options.wrapS;
    if (options.wrapT) texture.wrapT = options.wrapT;
    if (options.repeat) texture.repeat.set(options.repeat[0], options.repeat[1]);
    if (options.offset) texture.offset.set(options.offset[0], options.offset[1]);
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheStats() {
    return {
      size: BaseRoom.textureCache.size,
      keys: Array.from(BaseRoom.textureCache.keys())
    };
  }

  loadMaterials() {
    for (const [name, config] of Object.entries(this.config.textures)) {
      config.map = this.textureLoader.load(config.file);

      for (const [key, value] of Object.entries(config.opts)) {
        config.map[key] = value;
      }
      this.materials[name] = new THREE.MeshBasicMaterial({
        map: config.map,
      });
    }
  }

  getAttributes() {
    let attributes = JSON.parse(JSON.stringify(Alpine.store("defaultValues")));
    this.getConfiguration().forEach((item) => {
      Object.keys(item.attributes).forEach((itemAttr) => {
        attributes[itemAttr] += item.attributes[itemAttr];
      });
    });
    return attributes;
  }

  getConfiguration() {
    let currentComponents = [];

    Object.keys(this.slots_components).forEach((slot) => {
      const component = this.getComponent(slot, this.slots_components[slot]);
      if (component) currentComponents.push(component);
    });

    return currentComponents;
  }

  getComponent(slot, index) {
    return (this.components[slot] || [])[index];
  }

  updateLightMap() {
    this.group.children[0].children.forEach((child) => {
      if (
        this.slots_meshes["floor"].includes(child.name) ||
        this.slots_meshes["floor_boards"].includes(child.name)
      ) {
        child.material.map = this.getLightMapMaterial().map;
      } else {
        child.material = this.getLightMapMaterial();
      }
    });
  }

  selectComponent(slot, index) {
    let component = this.getComponent(slot, index);
    if (!component) return;

    // Unslot pre-existing item
    let previous_component = this.getComponent(
      slot,
      this.slots_components[slot],
    );
    if (previous_component && previous_component != component) {
      previous_component.onUnselect();
    }

    this.slots_components[slot] = this.components[slot].indexOf(component);
    
    // Remove components that no longer meet their requirements
    this.removeIncompatibleComponents();
    
    this.updateLightMap();
    this.applySlotMaterials();
  }

  unSelectComponent(slot) {
    let component = this.getComponent(slot, this.slots_components[slot]);
    if (!component) return;

    delete this.slots_components[slot];
    component.onUnselect();
    
    this.removeIncompatibleComponents();
    
    this.updateLightMap();
    this.applySlotMaterials();
  }

  removeIncompatibleComponents() {
    const slotsToRemove = [];
    
    Object.keys(this.slots_components).forEach(slot => {
      const componentIndex = this.slots_components[slot];
      const component = this.getComponent(slot, componentIndex);
      
      if (component && component.required_components) {
        const requirementsMet = this.checkRequiredComponents(component);
        if (!requirementsMet) {
          slotsToRemove.push(slot);
        }
      }
    });
    
    // Remove components that no longer meet requirements
    slotsToRemove.forEach(slot => {
      const component = this.getComponent(slot, this.slots_components[slot]);
      if (component) {
        delete this.slots_components[slot];
        component.onUnselect();
      }
    });
  }

  checkRequiredComponents(component) {
    if (!component.required_components) {
      return true
    }
    
    for (const [slotName, requiredValues] of Object.entries(component.required_components)) {
      if (!(slotName in this.slots_components)) {
        return false
      }
      
      const selectedValue = this.slots_components[slotName];
      if (!requiredValues.includes(selectedValue)) {
        return false
      }
    }
    
    return true
  }

  applySlotMaterials() {}
}
